// Knowledge base page paths and folders — ontwerp §4 ("Paden").
//
// A page path is relative to its space (workspace or project), has no
// leading '/', uses `[a-z0-9._-]` per segment, ends in `.md`, is at most
// 8 levels deep and 200 characters long. Folders don't exist on their own:
// they are prefixes of page paths, written with a trailing '/' ('' = root).
//
// `normalizeKbPath` only fixes harmless notation (whitespace, leading
// slashes, backslashes, doubled slashes, './'); it never lower-cases or
// renames, so a page is always stored under exactly the name the caller
// meant — anything else is a validation error with a readable message.

import { z } from 'zod'
import { KB_MAX_PATH_DEPTH, KB_MAX_PATH_LENGTH } from './limits.js'

const SEGMENT_RE = /^[a-z0-9._-]+$/

function collapse(input: string): string {
  return input
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
    .replace(/^(\.\/)+/, '')
    .replace(/^\/+/, '')
}

/** Notation clean-up only (see module header). The result may still be invalid. */
export function normalizeKbPath(input: string): string {
  return collapse(input)
}

/** Why `path` is not a valid page path, or `null` when it is. Expects a normalized path. */
export function kbPathError(path: string): string | null {
  if (path.length === 0) return 'Path is empty'
  if (path.length > KB_MAX_PATH_LENGTH) return `Path is longer than ${KB_MAX_PATH_LENGTH} characters`
  if (path.endsWith('/')) return 'Path must name a page (ending in .md), not a folder'
  if (!path.endsWith('.md')) return 'Path must end in .md'
  const segments = path.split('/')
  if (segments.length > KB_MAX_PATH_DEPTH) return `Path is more than ${KB_MAX_PATH_DEPTH} levels deep`
  for (const segment of segments) {
    if (segment === '') return 'Path contains an empty segment'
    if (segment === '.' || segment === '..') return 'Path may not contain "." or ".." segments'
    if (!SEGMENT_RE.test(segment)) return `Segment "${segment}" may only use a-z, 0-9, ".", "_" and "-"`
  }
  if (segments[segments.length - 1] === '.md') return 'File name is empty'
  return null
}

export function isValidKbPath(path: string): boolean {
  return kbPathError(normalizeKbPath(path)) === null
}

/** Normalizes and validates; throws an Error with a readable message. */
export function parseKbPath(input: string): string {
  const path = normalizeKbPath(input)
  const error = kbPathError(path)
  if (error) throw new Error(`Invalid knowledge base path "${input}": ${error}`)
  return path
}

/** Zod schema: normalizes, then validates a page path. */
export const KbPathSchema = z
  .string()
  .transform((value) => normalizeKbPath(value))
  .superRefine((value, ctx) => {
    const error = kbPathError(value)
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error })
  })

// ---------------------------------------------------------------------------
// Folders (prefixes)
// ---------------------------------------------------------------------------

/** '' (root) or 'a/b/' — normalized, always with a trailing '/'. */
export function normalizeKbFolder(input: string): string {
  const collapsed = collapse(input).replace(/\/+$/, '')
  return collapsed === '' ? '' : `${collapsed}/`
}

/** Why `folder` (normalized) is not a valid folder prefix, or `null`. */
export function kbFolderError(folder: string): string | null {
  if (folder === '') return null
  if (folder.length > KB_MAX_PATH_LENGTH) return `Folder is longer than ${KB_MAX_PATH_LENGTH} characters`
  const segments = folder.slice(0, -1).split('/')
  if (segments.length >= KB_MAX_PATH_DEPTH) return `Folder is ${KB_MAX_PATH_DEPTH} or more levels deep`
  for (const segment of segments) {
    if (segment === '' || segment === '.' || segment === '..') return 'Folder contains an empty, "." or ".." segment'
    if (!SEGMENT_RE.test(segment)) return `Segment "${segment}" may only use a-z, 0-9, ".", "_" and "-"`
  }
  return null
}

/** Zod schema: normalizes ('' = root, else trailing '/'), then validates a folder prefix. */
export const KbFolderSchema = z
  .string()
  .transform((value) => normalizeKbFolder(value))
  .superRefine((value, ctx) => {
    const error = kbFolderError(value)
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error })
  })

/** Whether `path` lies in `folder` (any depth). Root ('') contains everything. */
export function isInKbFolder(path: string, folder: string): boolean {
  return folder === '' || path.startsWith(folder)
}

/** The folder a page lives in: 'a/b/c.md' → 'a/b/', 'c.md' → ''. */
export function kbFolderOf(path: string): string {
  const index = path.lastIndexOf('/')
  return index === -1 ? '' : path.slice(0, index + 1)
}

/** File name of a page: 'a/b/c.md' → 'c.md'. */
export function kbFileName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1)
}

/** Every ancestor folder, outermost first: 'a/b/c.md' → ['a/', 'a/b/']. */
export function kbAncestorFolders(path: string): string[] {
  const segments = path.split('/').slice(0, -1)
  return segments.map((_, i) => `${segments.slice(0, i + 1).join('/')}/`)
}

/**
 * Moves `path` from folder/page `from` to `to`.
 * - Page move (`from` ends in `.md`): only `path === from` moves, to `to`.
 * - Folder move (`from` ends in '/'): every path under `from` gets the
 *   prefix `to` (also a folder) instead.
 * Returns `null` when `path` is not affected.
 */
export function moveKbPath(path: string, from: string, to: string): string | null {
  if (from.endsWith('/')) {
    if (!path.startsWith(from)) return null
    return `${to}${path.slice(from.length)}`
  }
  return path === from ? to : null
}

/** Title fallback from a file name: 'api-routes.md' → 'api routes'. */
export function kbTitleFromPath(path: string): string {
  const name = kbFileName(path).replace(/\.md$/, '')
  return name.replace(/[-_]+/g, ' ').trim() || name
}

/** Longest slug `kbSlug` returns. */
export const KB_SLUG_MAX_LENGTH = 60

/**
 * A path segment for a free-text name (a role, an agent, a memory entry
 * title): NFKD with accents dropped, lower case, every run of characters
 * outside `[a-z0-9._-]` becomes one '-', leading/trailing '-', '.' and '_'
 * are trimmed, at most `KB_SLUG_MAX_LENGTH` characters.
 * 'Security Reviewer' → 'security-reviewer', 'Café Ünïcode!' → 'cafe-unicode'.
 * Returns '' when nothing usable is left; callers must handle that (e.g. fall
 * back to an id), never put '' into a path.
 */
export function kbSlug(name: string): string {
  const trim = (value: string) => value.replace(/^[-._]+|[-._]+$/g, '')
  const slug = trim(
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/-{2,}/g, '-'),
  )
  return trim(slug.slice(0, KB_SLUG_MAX_LENGTH))
}
